import { Receipt } from 'lucide-react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { EntityChangeSchema } from '@/services/api/generated/entityChangesService';

import { formatEnumValue } from '../utils/formatting';
import { useChangedFields } from '../utils/useChangedFields';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';

const KEY_FIELDS: FieldConfig[] = [
  { key: 'expense_type', label: 'Type', isEnum: true },
  { key: 'status', label: 'Status', isEnum: true },
  { key: 'payment_method', label: 'Payment Method', isEnum: true },
  { key: 'payment_date', label: 'Payment Date', isDate: true },
  { key: 'amount_display', label: 'Amount' },
  { key: 'reason', label: 'Reason' },
];

const PAYEE_FIELDS: FieldConfig[] = [
  { key: 'payee_display_name', label: 'Payee' },
  { key: 'payee_linked_type', label: 'Linked Type', isEnum: true },
  { key: 'payee_contact_email', label: 'Email' },
  { key: 'payee_contact_phone', label: 'Phone' },
  { key: 'payee_contact_address', label: 'Address' },
  { key: 'payee_coverage_category', label: 'Coverage Category', isEnum: true },
];

function formatCurrency(value: unknown): string {
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }
    return value;
  }
  return '\u2014';
}

function getPayeeLabel(payee: Record<string, unknown> | null): string | null {
  if (!payee) return null;
  const payeeType = payee.type as string | undefined;
  if (payeeType === 'PROVIDER') {
    return (payee.name as string | undefined) ?? null;
  }
  if (payeeType === 'POLICE_DEPARTMENT') {
    return (payee.name as string | undefined) ?? null;
  }
  if (payeeType === 'COVERAGE') {
    return (payee.carrier_name as string | undefined) ?? null;
  }
  if (payeeType === 'CONTACT') {
    const contact = payee.contact_info as Record<string, unknown> | undefined;
    if (!contact) return null;
    return (
      (contact.name as string | undefined) ||
      (contact.email as string | undefined) ||
      (contact.phone as string | undefined) ||
      null
    );
  }
  return null;
}

function getContactField(contactInfo: Record<string, unknown>, key: string): string | null {
  const raw = contactInfo[key];
  return typeof raw === 'string' && raw.trim() ? raw : null;
}

function getContactName(contactInfo: Record<string, unknown>): string | null {
  const organizationName = getContactField(contactInfo, 'organization_name');
  if (organizationName) return organizationName;

  const givenName = getContactField(contactInfo, 'given_name');
  const familyName = getContactField(contactInfo, 'family_name');
  if (givenName || familyName) return [givenName, familyName].filter(Boolean).join(' ');

  return getContactField(contactInfo, 'name');
}

function getContactPhone(contactInfo: Record<string, unknown>): string | null {
  const phoneNumbers = contactInfo.phone_numbers;
  if (Array.isArray(phoneNumbers) && phoneNumbers.length > 0) {
    const first = phoneNumbers[0];
    if (first && typeof first === 'object' && 'value' in first) {
      const {value} = (first as { value?: unknown });
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  return getContactField(contactInfo, 'phone');
}

function getContactEmail(contactInfo: Record<string, unknown>): string | null {
  const emailAddresses = contactInfo.email_addresses;
  if (Array.isArray(emailAddresses) && emailAddresses.length > 0) {
    const first = emailAddresses[0];
    if (first && typeof first === 'object' && 'value' in first) {
      const {value} = (first as { value?: unknown });
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  return getContactField(contactInfo, 'email');
}

function getContactAddress(contactInfo: Record<string, unknown>): string | null {
  const postalAddresses = contactInfo.postal_addresses;
  if (Array.isArray(postalAddresses) && postalAddresses.length > 0) {
    const first = postalAddresses[0];
    if (first && typeof first === 'object') {
      const {street} = (first as { street?: unknown });
      const {city} = (first as { city?: unknown });
      const {state} = (first as { state?: unknown });
      const postalCode = (first as { postal_code?: unknown }).postal_code;
      const parts = [street, city, state, postalCode]
        .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
        .map((part) => part.trim());
      if (parts.length > 0) return parts.join(', ');
    }
  }
  return getContactField(contactInfo, 'address');
}

function getPayeeDetails(payee: Record<string, unknown> | null): Record<string, unknown> {
  if (!payee) return {};

  const type = typeof payee.type === 'string' ? payee.type : null;

  if (type === 'CONTACT') {
    const contactInfo =
      payee.contact_info && typeof payee.contact_info === 'object'
        ? (payee.contact_info as Record<string, unknown>)
        : null;

    if (!contactInfo) return {};

    return {
      payee_display_name: getContactName(contactInfo),
      payee_linked_type: type,
      payee_contact_email: getContactEmail(contactInfo),
      payee_contact_phone: getContactPhone(contactInfo),
      payee_contact_address: getContactAddress(contactInfo),
    };
  }

  if (type === 'PROVIDER' || type === 'POLICE_DEPARTMENT') {
    return {
      payee_display_name:
        typeof payee.name === 'string' && payee.name.trim() ? payee.name : null,
      payee_linked_type: type,
    };
  }

  if (type === 'COVERAGE') {
    return {
      payee_display_name:
        typeof payee.carrier_name === 'string' && payee.carrier_name.trim()
          ? payee.carrier_name
          : null,
      payee_linked_type: type,
      payee_coverage_category:
        typeof payee.category === 'string' && payee.category.trim() ? payee.category : null,
    };
  }

  return {};
}

interface CaseExpenseChangeCardProps {
  entity: EntityChangeSchema;
}

export const CaseExpenseChangeCard: React.FC<CaseExpenseChangeCardProps> = ({
  entity,
}) => {
  const changes = useChangedFields(entity.change_type, entity.changed_fields ?? []);
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const payee = (data.payee as Record<string, unknown> | undefined) ?? null;
  const payeeType = typeof payee?.type === 'string' ? payee.type : null;
  const payeeLabel = getPayeeLabel(payee);
  const expenseType = typeof data.expense_type === 'string' ? data.expense_type : null;
  const payeeDetails = getPayeeDetails(payee);

  const displayData = {
    ...data,
    amount_display: formatCurrency(data.amount),
    ...payeeDetails,
  };

  const title = payeeLabel || formatEnumValue(data.expense_type as string) || 'Case Expense';

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={title}
      icon={Receipt}
      iconBgClassName="bg-rose-50"
      iconTextClassName="text-rose-600"
      entityStatus={effectiveStatus}
      onStatusChange={
        actions
          ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
          : undefined
      }
      badges={
        <>
          {expenseType && (
            <Badge color="zinc" className="text-xs">
              {formatEnumValue(expenseType)}
            </Badge>
          )}
          {payeeType && (
            <Badge color="zinc" className="text-xs">
              {formatEnumValue(payeeType)}
            </Badge>
          )}
        </>
      }
      changeCount={changes.totalChanges}
      hasExpandedContent
    >
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={displayData}
        changes={changes}
        changeType={entity.change_type}
      />

      <ShowMoreSection changeType={entity.change_type}>
        <KeyFieldsGridUnchanged
          fields={KEY_FIELDS}
          data={displayData}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGrid
          fields={PAYEE_FIELDS}
          data={displayData}
          changes={changes}
          changeType={entity.change_type}
        />
        <KeyFieldsGridUnchanged
          fields={PAYEE_FIELDS}
          data={displayData}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>
    </EntityChangeCardShell>
  );
};
